import React, { FC, useCallback, useState } from "react";
import { Stack, TextField, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormikHandlers } from "formik";
import { CommandSnippet } from "@/components/pages/common/connectors/components/CommandSnippet";
import { SnowflakeConnectionFormInfoCard } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/form/SnowflakeConnectionFormInfoCard";

interface SnowflakeAccountIdentifierFieldProps {
  accountIdentifier: string;
  handleChange: FormikHandlers["handleChange"];
  error?: boolean;
}

export const SnowflakeAccountIdentifierField: FC<
  SnowflakeAccountIdentifierFieldProps
> = ({ accountIdentifier, handleChange, error }) => {
  const [
    isAccountIdentifierDescriptionCollapsed,
    setIsAccountIdentifierDescriptionCollapsed,
  ] = useState(true);

  const handleFieldDescriptionCollapse = useCallback(() => {
    setIsAccountIdentifierDescriptionCollapsed((prev) => !prev);
  }, []);

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography>Account Identifier</Typography>
        <Tooltip
          title="An account identifier uniquely identifies a Snowflake account within your organization, as well as throughout the global network of Snowflake-supported cloud platforms and cloud regions.
The preferred account identifier consists of the name of the account prefixed by its organization (e.g. myorg-account123)"
          placement="top"
          arrow
        >
          <InfoOutlinedIcon color="action" sx={{ fontSize: 16 }} />
        </Tooltip>
      </Stack>
      <TextField
        fullWidth
        value={accountIdentifier ?? ""}
        type="text"
        size="small"
        name="account_identifier"
        error={error}
        onChange={handleChange}
      />

      <SnowflakeConnectionFormInfoCard
        title={
          <Typography
            variant="body2"
            width="100%"
            display="block"
            color="text.secondary"
          >
            How to find the{" "}
            <Typography variant="inherit" component="span" fontWeight="medium">
              Account Identifier
            </Typography>
            ?
          </Typography>
        }
        description={
          <CommandSnippet
            code="SELECT 'Org:' || CURRENT_ORGANIZATION_NAME() || ' Account:' || CURRENT_ACCOUNT_NAME();"
            parentBoxSx={{ m: 0 }}
          >
            <Typography
              sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
              variant="body2"
            >
              SELECT 'Org:' || CURRENT_ORGANIZATION_NAME() || ' Account:' ||
              CURRENT_ACCOUNT_NAME();
            </Typography>
          </CommandSnippet>
        }
        isCollapsed={isAccountIdentifierDescriptionCollapsed}
        handleCollapse={handleFieldDescriptionCollapse}
      />
    </Stack>
  );
};
